import CryptoJS from 'crypto-js';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { MenuItemProps } from '../components/nav/SideMenuItems';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { ModuleAccess } from '../types/auth.model';
import { GetProp, UploadProps } from 'antd';

export const baseUrl = process.env.REACT_APP_API_URL;

export type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
export const dateFormat = 'YYYY-MM-DD';
export const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
export const dateNameTimeFormat = 'MMM DD, YYYY HH:mm:ss';

// Encryption key (should be kept secure)
const secretKey: string =
  process.env.REACT_APP_SECRET_KEY || 'rotYsn73RDfqbXmEcS6IBS8603zsBnKL';

// Encrypt data
export function encryptData(data: string): string {
  return CryptoJS.AES.encrypt(data, secretKey).toString();
}

// Decrypt data
export function decryptData(encryptedData: string): string {
  const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
  return bytes.toString(CryptoJS.enc.Utf8);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const findKeyFromUrl = (
  items: MenuItemProps[],
  pathname: string,
): string => {
  const segments = pathname.split('/');
  const filteredSegments = segments.filter((segment) => !/^\d+$/.test(segment));
  let baseUrl = filteredSegments.join('/');
  const lastSegments = baseUrl.split('/');
  if (lastSegments[lastSegments.length - 1] === 'request') {
    baseUrl = baseUrl.replace('/request', '');
  }
  for (const item of items) {
    const itemSegments = item.url.split('/');
    const filteredItemSegments = itemSegments.filter(
      (segment) => !/^\d+$/.test(segment),
    );
    const itemBaseUrl = filteredItemSegments.join('/');
    if (itemBaseUrl === baseUrl) {
      return <string>item.key;
    } else if (item.children) {
      const childKey = findKeyFromUrl(item.children, pathname);
      if (childKey) {
        return childKey;
      }
    }
  }
  return '';
};

export const findPageNameFromUrl = (
  items: MenuItemProps[],
  pathname: string,
): string => {
  for (const item of items) {
    if (item.url === pathname) {
      return <string>item.label;
    } else if (item.children) {
      const childPage = findPageNameFromUrl(item.children, pathname);
      if (childPage) {
        return childPage;
      }
    }
  }
  return '';
};

export const convertDate = (stringDate: string, dateTimeFormat: string) => {
  return dayjs(stringDate).format(dateTimeFormat);
};

export function roundAndFormat(
  number: number | undefined,
  factor = 100,
  fractionDigits = 2,
) {
  if (number === undefined) {
    return 0;
  }
  const roundedNumber = Math.round(number * factor) / factor;
  return roundedNumber.toFixed(fractionDigits);
}

export function capitalizeFirstLetter(word: '' | undefined | string | null) {
  if (word === undefined || word === '' || word === null) return 'View';
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export const exportToExcel = ({
  data,
  headers,
  fileName,
}: {
  data: any[];
  headers: string[];
  fileName: string;
}) => {
  const wb = XLSX.utils.book_new();
  const formattedData = data.map((item) =>
    headers.map((header) => item[header]),
  );
  const sheetData = [headers, ...formattedData];

  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  XLSX.utils.book_append_sheet(wb, ws);
  XLSX.writeFile(wb, `${fileName}.xlsx`);
};

export function getEnumValue<T extends Record<string, string>>(
  enumType: T,
  value: string,
): T[keyof T] | string {
  const enumValues = Object.values(enumType) as string[];
  const enumKeys = Object.keys(enumType) as (keyof T)[];

  for (let i = 0; i < enumValues.length; i++) {
    if (enumValues[i] === value) {
      return enumType[enumKeys[i]] === 'ALL' ? '' : enumType[enumKeys[i]];
    }
  }
  return '';
}

export function isModuleApproved(
  moduleAccess: ModuleAccess[] | undefined,
  moduleName: string,
): boolean {
  if (moduleAccess === undefined) return false;
  return moduleAccess.some(
    (module) => module.module.description === moduleName && module.approve,
  );
}

export function isUserCanEdit(
  moduleAccess: ModuleAccess[] | undefined,
  moduleName: string,
): boolean {
  if (moduleAccess === undefined) return false;
  return moduleAccess.some(
    (module) => module.module.description === moduleName && module.write,
  );
}

export function isUserCanCreate(
  moduleAccess: ModuleAccess[] | undefined,
  moduleName: string,
): boolean {
  if (moduleAccess === undefined) return false;
  return moduleAccess.some(
    (module) => module.module.description === moduleName && module.create,
  );
}

export const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export function getTextWidth(
  doc: any,
  text: string,
  fontSize: number,
  fontType: string,
): number {
  // Save current font settings
  const currentFontSize = doc.internal.getFontSize();
  const currentFontType = doc.internal.getFont().fontName;

  // Set the desired font style
  doc.setFont(fontType);
  doc.setFontSize(fontSize);

  // Calculate the width of the text
  const textWidth =
    (doc.getStringUnitWidth(text) * fontSize) / doc.internal.scaleFactor;

  // Restore previous font settings
  doc.setFont(currentFontType);
  doc.setFontSize(currentFontSize);

  return textWidth;
}
